import { useEffect, useMemo, useState } from 'react';
import { Barcode, Loader2, PackagePlus, ScanLine, Save } from 'lucide-react';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { BarcodeScanner } from '@/components/BarcodeScanner';
import type { WarehouseItem } from '@/api/warehouse';
import { useCreateWarehouseItem } from '../hooks/useWarehouse';

interface CreateWarehouseItemSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialBarcode?: string;
  onCreated?: (item: WarehouseItem) => void;
}

const WAREHOUSE_BARCODE_PATTERN = /^DS[0-9A-Z]{6}$/;
const WAREHOUSE_BARCODE_EXAMPLE = 'DS0000PN';

function normalizeBarcode(value: string): string {
  return value.trim().toUpperCase();
}

export function CreateWarehouseItemSheet({
  open,
  onOpenChange,
  initialBarcode,
  onCreated,
}: CreateWarehouseItemSheetProps) {
  const createMutation = useCreateWarehouseItem();
  const [name, setName] = useState('');
  const [barcode, setBarcode] = useState('');
  const [quantity, setQuantity] = useState('');
  const [comment, setComment] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName('');
    setBarcode(initialBarcode ? normalizeBarcode(initialBarcode) : '');
    setQuantity('');
    setComment('');
  }, [initialBarcode, open]);

  const normalizedBarcode = useMemo(() => normalizeBarcode(barcode), [barcode]);
  const isBarcodeValid = WAREHOUSE_BARCODE_PATTERN.test(normalizedBarcode);
  const barcodeError =
    barcode.trim().length > 0 && !isBarcodeValid
      ? `Формат: ${WAREHOUSE_BARCODE_EXAMPLE}, только 0-9 и A-Z`
      : null;
  const canSave =
    name.trim().length > 0 && isBarcodeValid && !createMutation.isPending;

  const handleSave = () => {
    if (!canSave) return;
    createMutation.mutate(
      {
        name: name.trim(),
        barcode: normalizedBarcode,
        quantity: quantity.trim() || undefined,
        comment: comment.trim() || undefined,
        isInOffice: true,
        isWorkUnit: false,
      },
      {
        onSuccess: (item) => {
          onOpenChange(false);
          onCreated?.(item);
        },
      },
    );
  };

  const handleScan = (value: string) => {
    const nextBarcode = normalizeBarcode(value);
    setBarcode(nextBarcode);
    setScannerOpen(false);
    if (!WAREHOUSE_BARCODE_PATTERN.test(nextBarcode)) {
      toast.error('Сканированный код не похож на складскую наклейку');
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto">
          <SheetHeader className="text-left">
            <SheetTitle className="flex items-center gap-2">
              <PackagePlus className="h-5 w-5" />
              Новый товар на склад
            </SheetTitle>
          </SheetHeader>

          <div className="mt-4 space-y-4 pb-4">
            <div>
              <Label htmlFor="warehouse-name">Название *</Label>
              <Input
                id="warehouse-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Например: блок питания 12V"
              />
            </div>

            <div>
              <div className="mb-1 flex items-center justify-between gap-2">
                <Label htmlFor="warehouse-barcode">Штрихкод *</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setScannerOpen(true)}
                >
                  <ScanLine className="mr-2 h-4 w-4" />
                  Сканировать
                </Button>
              </div>
              <div className="relative">
                <Barcode className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="warehouse-barcode"
                  value={barcode}
                  onChange={(event) => setBarcode(event.target.value.trim().toUpperCase())}
                  placeholder={WAREHOUSE_BARCODE_EXAMPLE}
                  autoCapitalize="characters"
                  className="pl-10 font-mono"
                />
              </div>
              {barcodeError && <p className="mt-1 text-xs text-destructive">{barcodeError}</p>}
            </div>

            <div>
              <Label htmlFor="warehouse-quantity">Количество</Label>
              <Input
                id="warehouse-quantity"
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
                placeholder="Например: 1 шт."
              />
            </div>

            <div>
              <Label htmlFor="warehouse-comment">Комментарий</Label>
              <Textarea
                id="warehouse-comment"
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                rows={4}
              />
            </div>

            <Button type="button" className="w-full" onClick={handleSave} disabled={!canSave}>
              {createMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Сохранить
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {scannerOpen && <BarcodeScanner onScan={handleScan} onClose={() => setScannerOpen(false)} />}
    </>
  );
}
